import React from 'react'
import { motion } from 'framer-motion'

/**
 * Section. Phase 18-Z7 redesign (2026-05-19): premium feature card.
 * Rendered inside a responsive grid by App.jsx PageView.
 * data-cms-field attrs preserved for CMS inline editing.
 */

// 8 inline SVG icons (VR / tech / sports theme), picked by index.
const ICONS = [
    // 0 target
    <g key="i0"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="0.6" fill="currentColor" /></g>,
    // 1 expand / unlimited
    <g key="i1"><path d="M4 14v6h6M20 10V4h-6" /><path d="M20 4l-7 7M4 20l7-7" /></g>,
    // 2 bolt / challenge
    <g key="i2"><path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" /></g>,
    // 3 spark / new
    <g key="i3"><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M18 6l-2.5 2.5M8.5 15.5L6 18" /><circle cx="12" cy="12" r="2.5" /></g>,
    // 4 cube / virtual
    <g key="i4"><path d="M12 2l9 5v10l-9 5-9-5V7l9-5z" /><path d="M12 12l9-5M12 12v10M12 12L3 7" /></g>,
    // 5 shield / safer
    <g key="i5"><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z" /><path d="M9 12l2 2 4-4" /></g>,
    // 6 globe / anywhere
    <g key="i6"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.5 2.7 2.5 15.3 0 18M12 3c-2.5 2.7-2.5 15.3 0 18" /></g>,
    // 7 chart check / verified
    <g key="i7"><path d="M4 20V10M10 20V4M16 20v-6M22 20V8" /><path d="M3 20h19" /></g>,
]

export default function Section({ section, index = 0 }) {
    if (!section) return null
    const icon = ICONS[index % ICONS.length]
    return (
        <motion.article
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, delay: (index % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="card-hover group relative flex flex-col h-full p-8 rounded-2xl bg-white border border-slate-200"
        >
            {/* number + icon */}
            <div className="flex items-center justify-between mb-6">
                <span className="text-xs font-bold tracking-widest text-slate-300">
                    {String(index + 1).padStart(2, '0')}
                </span>
                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-brand-500 to-cyan-500 text-white shadow-lg shadow-brand-500/25 group-hover:scale-110 transition-transform">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        {icon}
                    </svg>
                </div>
            </div>
            <h3
                data-cms-field={`sections.${index}.title`}
                className="text-lg md:text-xl font-bold text-slate-900 leading-snug"
            >
                {section.title || ''}
            </h3>
            <p
                data-cms-field={`sections.${index}.body`}
                className="mt-3 text-sm md:text-base text-slate-600 leading-relaxed flex-1"
            >
                {section.body || ''}
            </p>
            {/* bottom accent line */}
            <div className="mt-6 h-0.5 w-12 rounded-full bg-gradient-to-r from-brand-500 to-cyan-400 group-hover:w-full transition-all duration-500" />
        </motion.article>
    )
}
